// Citation types for Advisory mode memory references

export interface MemoryCitation {
  type: "memory"
  id: string
  memoryId?: string // Optional: specific memory record ID
  domainSlug?: string // Optional: domain slug for section-level links
  label: string // Display label, truncated if needed
  fullLabel?: string // Full label for tooltip
  savedAt?: string // ISO date string
}

export interface WebCitation {
  type: "web"
  id: string
  url: string
  title: string
  source: string // e.g., "ProfitWell Report"
  year?: number
}

export interface ExternalCitation {
  type: "external"
  url: string
  label: string
}

export type Citation = MemoryCitation | WebCitation

export type SourceCitation =
  | { type: "external"; url: string; label: string }
  | { type: "memory"; memory_id?: string; domainSlug?: string; label: string }

export interface CitedMessage {
  messageId: string
  content: string
  citations: Citation[]
  sources?: SourceCitation[] // Structured sources metadata
}

export function getMemoryHref(citation: { memory_id?: string; memoryId?: string; domainSlug?: string }): string {
  // Prefer specific memory link
  if (citation.memory_id || citation.memoryId) {
    return `/brain?memory=${citation.memory_id || citation.memoryId}`
  }
  // Fallback to domain section
  if (citation.domainSlug) {
    return `/brain?scope=domain:${citation.domainSlug}`
  }
  // Default to overview
  return "/brain"
}

// Helper to determine if link is memory-specific (Brain icon) vs section (Book icon)
export function isMemorySpecificLink(href: string): boolean {
  return href.includes("memory=")
}

// Helper to generate citation display text
export function getCitationDisplayText(citation: Citation): string {
  if (citation.type === "memory") {
    return citation.label
  }
  return `Web: ${citation.source}${citation.year ? ` ${citation.year}` : ""}`
}

// Helper to format memory label from section/field
export function formatMemoryLabel(sectionSlug: string, fieldKey?: string): string {
  const sectionNames: Record<string, string> = {
    identity: "Identity",
    people: "Owner Profile",
    offerings: "Offerings",
    audience: "Audience",
    brand: "Brand",
    competition: "Competition",
    technology: "Technology",
    marketing: "Marketing",
    sales: "Sales",
    operations: "Operations",
    finance: "Finance",
    goals: "Goals",
  }

  const sectionName = sectionNames[sectionSlug] || sectionSlug

  if (fieldKey) {
    // Convert field key to readable label
    const fieldLabel = fieldKey
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
    return `${sectionName} → ${fieldLabel}`
  }

  return `${sectionName} Memory`
}
